import { NUM_SEATS, MIN_BET, MAX_BET, BET_STEP, BLACKJACK_DEAL_MS, appearanceHash, DEFAULT_APPEARANCE } from "@ghostland/shared";
import { blackjackStore, type TableView, type SeatView, type HandView } from "../items/blackjackStore.js";
import { balanceStore } from "../items/balanceStore.js";
import { drawCard, drawCardBack } from "../items/cardArt.js";
import { drawPokerTable } from "../items/tableArt.js";
import { composeBust } from "../sprites/portrait.js";
import { injectTheme } from "./theme.js";
import { injectBillStyle, cashIcon } from "./pixelBills.js";

/**
 * The blackjack table overlay — a vintage pixel-RPG scene: a felt poker table, a
 * suited skeleton croupier, and a character "card" at each seat. The acting
 * player's own hand is mirrored large at the bottom. Cards deal in with a staggered
 * slide animation, in sequence, by appending only newly-arrived cards (existing
 * cards are never re-created, so they don't re-animate).
 */

const STYLE_ID = "ghostland-bj-style";
const DEAL_INTERVAL = BLACKJACK_DEAL_MS; // ms between each card revealing (matches the server's reset pad)
// The dealer is a real character in a black formal suit (top style 6) — matches Roxy, the casino host.
const DEALER_LOOK = { ...DEFAULT_APPEARANCE, face: 1, skinTone: 1, hair: 1, hairColor: 5, top: 6, topColor: 0, bottom: 1, bottomColor: 1, shoes: 1, shoesColor: 1 };

// Seat anchor points (% of the stage), arranged along the lower arc of the table.
const SEAT_POS: ReadonlyArray<{ l: number; t: number }> = [
  { l: 13, t: 46 },
  { l: 31, t: 72 },
  { l: 50, t: 78 }, // bottom-centre seat raised so its cards clear the player's big hand below
  { l: 69, t: 72 },
  { l: 87, t: 46 },
];

const CSS = `
#gl-bj-overlay {
  position: fixed; inset: 0; z-index: 30; display: none;
  align-items: center; justify-content: center;
  background: rgba(5,0,7,.78); backdrop-filter: blur(2px);
  font-family: ui-monospace, "SFMono-Regular", Menlo, monospace; color: var(--gl-text);
}
#gl-bj-overlay.open { display: flex; }
#gl-bj-scene {
  width: min(960px, 96vw); max-height: 96vh; box-sizing: border-box; padding: 14px 18px 12px;
  background: linear-gradient(#0b0b0b, #000000);
  border: 3px solid #ffffff; border-radius: 10px;
  box-shadow: 0 0 0 2px #ffffff, 0 20px 60px rgba(0,0,0,.75);
  image-rendering: pixelated;
}
#gl-bj-head { display: flex; align-items: baseline; justify-content: space-between; margin-bottom: 6px; }
#gl-bj-head h2 { margin: 0; font-size: 15px; letter-spacing: 2px; color: #ffffff; text-shadow: 1px 1px 0 #000; }
#gl-bj-phase { font-size: 12px; color: #b0b0b0; }

#gl-bj-stage { position: relative; width: 100%; height: clamp(280px, 54vh, 480px); }
#gl-bj-table-img {
  position: absolute; left: 50%; top: 44%; transform: translate(-50%, -50%);
  height: 80%; max-width: 72%; width: auto; image-rendering: pixelated; pointer-events: none;
}

@keyframes gl-deal {
  from { transform: translateY(-46px) scale(.78); opacity: 0; }
  to   { transform: translateY(0) scale(1); opacity: 1; }
}
canvas.gl-deal { animation: gl-deal .3s ease-out both; }
/* The dealer's hole turning face-up: a half-turn flip in place (no slide-in). */
@keyframes gl-flip {
  from { transform: rotateY(90deg); }
  to   { transform: rotateY(0deg); }
}
canvas.gl-flip { animation: gl-flip .28s ease-out both; }
.gl-bj-hand { display: flex; }
.gl-bj-hand canvas { image-rendering: pixelated; }

#gl-bj-dealer {
  position: absolute; left: 50%; top: 0; transform: translateX(-50%);
  display: flex; flex-direction: column; align-items: center; gap: 2px; z-index: 2;
}
#gl-bj-dealer .skeleton { width: 78px; height: 98px; image-rendering: pixelated; } /* 0.8 bust aspect — not squished */
#gl-bj-dealer .val { font-size: 13px; color: #ffffff; text-shadow: 1px 1px 0 #000; min-height: 15px; }
#gl-bj-dealer .gl-bj-hand { min-height: 64px; }
#gl-bj-dealer .gl-bj-hand canvas { width: 46px; height: 64px; margin-left: -16px; }
#gl-bj-dealer .gl-bj-hand canvas:first-child { margin-left: 0; }

/* Character "card" at a seat — parchment + dark wood frame, vintage RPG style. */
.gl-bj-seat {
  position: absolute; transform: translate(-50%, -50%); z-index: 1;
  width: 124px; box-sizing: border-box; padding: 5px 5px 6px;
  display: flex; flex-direction: column; align-items: center; gap: 2px;
  background: linear-gradient(#d8d8d8, #b8b8b8);
  border: 2px solid #888888; box-shadow: 0 0 0 2px #ffffff, 0 3px 0 rgba(0,0,0,.5);
  color: #0b0b0b;
}
.gl-bj-seat.empty {
  background: rgba(20,10,4,.5); border-color: #ffffff; box-shadow: 0 0 0 2px #0b0b0b; color: #888888;
}
.gl-bj-seat.empty .frame, .gl-bj-seat.empty .name, .gl-bj-seat.empty .hands, .gl-bj-seat.empty .bet { display: none; }
.gl-bj-seat.empty .open-seat { display: block; }
.gl-bj-seat .open-seat { display: none; font-size: 9px; letter-spacing: 1px; padding: 16px 0; }
.gl-bj-seat.mine { box-shadow: 0 0 0 2px #ffffff, 0 3px 0 rgba(0,0,0,.5); }
.gl-bj-seat.active { box-shadow: 0 0 0 2px #ffffff, 0 0 12px 2px rgba(196,181,253,.6); }
.gl-bj-seat .frame {
  width: 44px; height: 60px; box-sizing: border-box; background: #000000; border: 1px solid #888888;
  display: flex; align-items: center; justify-content: center;
}
.gl-bj-seat .frame canvas { width: 38px; height: 52px; image-rendering: pixelated; }
.gl-bj-seat .name {
  font-size: 11px; font-weight: 700; max-width: 100%;
  overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
}
.gl-bj-seat .badge { font-size: 8px; padding: 0 4px; border-radius: 3px; }
.gl-bj-seat .badge.you { background: #ffffff; color: #000; }
.gl-bj-seat .badge.spec { background: #555555; color: #e8e8e8; }
.gl-bj-seat .badge.gone { background: var(--gl-error); color: #fff; }
.gl-bj-seat .hands { display: flex; gap: 4px; align-items: flex-start; justify-content: center; min-height: 54px; }
.gl-bj-seat .handcol { display: flex; flex-direction: column; align-items: center; }
.gl-bj-seat .handcol[hidden] { display: none; }
.gl-bj-seat .handcol.active .gl-bj-hand { outline: 1px solid #ffffff; }
.gl-bj-seat .gl-bj-hand canvas { width: 38px; height: 53px; margin-left: -13px; }
.gl-bj-seat .gl-bj-hand canvas:first-child { margin-left: 0; }
.gl-bj-seat .val { font-size: 11px; color: #ffffff; min-height: 13px; }
.gl-bj-seat .val.won { color: #ffffff; font-weight: 700; }
.gl-bj-seat .val.lost { color: #888888; }
.gl-bj-seat .val.push { color: #ffffff; }
.gl-bj-seat .bet {
  font-size: 13px; color: #ffe27a; font-weight: 700; min-height: 15px;
  display: inline-flex; align-items: center; gap: 4px; text-shadow: 0 1px 0 #000;
}
.gl-bj-seat .bet:not(:empty) { background: rgba(0,0,0,.55); border-radius: 8px; padding: 1px 7px; }

/* The acting player's hand, mirrored large at the bottom. */
#gl-bj-myhand {
  display: none; align-items: flex-end; justify-content: center; gap: 20px; min-height: 96px; margin-top: 22px;
  position: relative; z-index: 3; /* the player's big hand sits clearly below + above the bottom seat */
}
#gl-bj-myhand.show { display: flex; }
#gl-bj-myhand .handcol { display: flex; flex-direction: column; align-items: center; gap: 2px; }
#gl-bj-myhand .handcol.active { background: rgba(124,58,237,.18); border-radius: 6px; padding: 2px 6px; }
#gl-bj-myhand .gl-bj-hand canvas { width: 60px; height: 84px; margin-left: -22px; }
#gl-bj-myhand .gl-bj-hand canvas:first-child { margin-left: 0; }
#gl-bj-myhand .val { font-size: 13px; font-weight: 700; color: #e8e8e8; }
#gl-bj-myhand .val.won { color: #ffffff; }
#gl-bj-myhand .val.lost { color: var(--gl-error); }
#gl-bj-myhand .val.push { color: #ffffff; }

#gl-bj-controls {
  display: flex; align-items: center; justify-content: center; gap: 10px;
  min-height: 44px; margin-top: 8px; flex-wrap: wrap;
}
#gl-bj-controls .hint { font-size: 13px; color: #b0b0b0; }
#gl-bj-controls .countdown { font-size: 13px; font-weight: 700; color: #ffffff; }
.gl-bj-stepper { display: flex; align-items: center; gap: 6px; }
.gl-bj-stepper .amt { min-width: 64px; text-align: center; font-size: 16px; font-weight: 700; color: #ffffff; cursor: text; border-radius: 4px; padding: 2px 4px; }
.gl-bj-stepper .amt:hover { background: rgba(255,255,255,.10); }
.gl-bj-stepper .amt-input {
  width: 72px; text-align: center; font-size: 16px; font-weight: 700; color: #ffffff;
  background: #0b0b0b; border: 2px solid #ffffff; border-radius: 4px; padding: 1px 4px;
  font-family: inherit; outline: none; -moz-appearance: textfield;
}
.gl-bj-stepper .amt-input::-webkit-outer-spin-button,
.gl-bj-stepper .amt-input::-webkit-inner-spin-button { -webkit-appearance: none; margin: 0; }
.gl-bj-step {
  width: 30px; height: 30px; border-radius: 4px; border: 2px solid #ffffff;
  background: #0b0b0b; color: #e8e8e8; font-size: 16px; cursor: pointer;
}
.gl-bj-step:hover:not(:disabled) { border-color: #ffffff; }
.gl-bj-step:disabled { opacity: .4; cursor: default; }
.gl-bj-action {
  padding: 9px 18px; border-radius: 5px; border: none; font-family: inherit;
  font-size: 14px; font-weight: 700; cursor: pointer; color: #fff;
  text-shadow: 0 1px 0 rgba(0,0,0,.45);
}
.gl-bj-action.bet { background: #1f9b4a; }   /* green */
.gl-bj-action.hit { background: #c0392b; }   /* red */
.gl-bj-action.stand { background: #2c6fb0; } /* blue */
.gl-bj-action.dbl { background: #d4a017; color: #20160a; text-shadow: none; } /* gold */
.gl-bj-action.spl { background: #7c3aed; }   /* purple */
.gl-bj-action:hover { filter: brightness(1.12); }
.gl-bj-action:disabled { opacity: .45; cursor: default; }

#gl-bj-foot { display: flex; justify-content: flex-end; gap: 8px; margin-top: 8px; }
.gl-bj-btn {
  padding: 8px 16px; border-radius: 5px; border: 2px solid #6a6a6a;
  background: #14140f; color: #e8e8e8; font-family: inherit; font-size: 12px; cursor: pointer;
}
.gl-bj-btn:hover { border-color: #aaaaaa; }
.gl-bj-btn.primary { background: #b23a3a; border-color: #d46a6a; color: #ffffff; }
.gl-bj-btn.primary:hover { background: #c84545; }
.gl-bj-btn[hidden] { display: none; }

/* Big, centre-of-screen round result. */
#gl-bj-result {
  position: fixed; left: 50%; top: 42%; transform: translate(-50%, -50%); z-index: 60;
  pointer-events: none; opacity: 0; text-align: center; font-family: inherit; font-weight: 800;
}
#gl-bj-result.show { animation: gl-bj-pop 3.2s ease-out forwards; }
#gl-bj-result .word { font-size: clamp(34px, 6.5vw, 68px); letter-spacing: 3px; text-shadow: 0 4px 0 #000, 0 0 26px rgba(0,0,0,.85); }
#gl-bj-result .amt { font-size: clamp(20px, 3.6vw, 38px); margin-top: 8px; display: inline-flex; align-items: center; gap: 10px; text-shadow: 0 3px 0 #000; }
#gl-bj-result.win .word, #gl-bj-result.win .amt { color: #ffe27a; }
#gl-bj-result.loss .word, #gl-bj-result.loss .amt { color: #ff7a7a; }
#gl-bj-result.push .word, #gl-bj-result.push .amt { color: #cfe0ff; }
@keyframes gl-bj-pop {
  0%   { opacity: 0; transform: translate(-50%, -50%) scale(.55); }
  12%  { opacity: 1; transform: translate(-50%, -50%) scale(1.12); }
  22%  { transform: translate(-50%, -50%) scale(1); }
  82%  { opacity: 1; transform: translate(-50%, -50%) scale(1); }
  100% { opacity: 0; transform: translate(-50%, -50%) scale(1.04); }
}
`;

const PHASE_LABEL: Record<string, string> = {
  idle: "Waiting for players",
  betting: "Place your bets",
  dealing: "Dealing…",
  turns: "Players' turns",
  dealer: "Dealer plays",
  settle: "Resolving",
  payout: "Paying out",
  reset: "Round over",
};

/** Intents the overlay can fire. Registered by WorldScene. */
export interface TableActions {
  leave?: () => void;
  bet?: (amount: number) => void;
  hit?: () => void;
  stand?: () => void;
  double?: () => void;
  split?: () => void;
}

let actions: TableActions = {};
export function setTableActions(a: TableActions): void {
  actions = a;
}

function ensureStyle(): void {
  injectTheme();
  injectBillStyle();
  if (document.getElementById(STYLE_ID)) return;
  const style = document.createElement("style");
  style.id = STYLE_ID;
  style.textContent = CSS;
  document.head.appendChild(style);
}

let overlay: HTMLDivElement | null = null;
let seatNodes: HTMLDivElement[] = [];
let dealerHandEl: HTMLDivElement | null = null;
let dealerValEl: HTMLSpanElement | null = null;
let myHandEl: HTMLDivElement | null = null;
let phaseEl: HTMLDivElement | null = null;
let leaveBtn: HTMLButtonElement | null = null;
let controlsEl: HTMLDivElement | null = null;
let resultBanner: HTMLDivElement | null = null;

let current: TableView | null = null;
let selectedBet = MIN_BET;
// Whether the end-of-round result toast has been shown for the current round.
let resultShown = false;

// --- Paced card-dealing queue -------------------------------------------------
// New cards (across all hands + the dealer) reveal one at a time at a constant
// interval, so the deal is visibly sequential even on a blackjack. Each job
// appends one card element to a specific row; resetting a row drops its pending
// jobs. The hand's value label is withheld until the hand is fully dealt.
interface DealJob {
  row: HTMLElement;
  run: () => void;
}
let dealQueue: DealJob[] = [];
let dealTimer: number | undefined;

function enqueueDeal(row: HTMLElement, run: () => void): void {
  dealQueue.push({ row, run });
  if (dealTimer === undefined) pumpDeal();
}

function pumpDeal(): void {
  const job = dealQueue.shift();
  if (!job) {
    dealTimer = undefined;
    // Every card (incl. the dealer's) has landed — now it's safe to reveal the
    // round result: settle labels + the net-result toast.
    if (current) {
      revealOwnSeat();
      refreshLabels(current);
      maybeShowResult(current);
    }
    return;
  }
  job.run();
  if (current) {
    revealOwnSeat();                // step the OWN seat card forward with the big hand
    refreshLabels(current);          // reveal a hand's value once it's fully dealt
  }
  // The DEALER reveals its own cards a full second slower between each — more suspense as it draws.
  const delay = job.row === dealerHandEl ? DEAL_INTERVAL + 1000 : DEAL_INTERVAL;
  dealTimer = window.setTimeout(pumpDeal, delay);
}

/** Re-render the OWN seat so its cards track the big hand's current reveal (called each deal tick). */
function revealOwnSeat(): void {
  const table = current;
  if (!table || table.mySeat === -1) return;
  const seat = table.seats.find((s) => s.index === table.mySeat);
  if (seat) renderSeat(seatNodes[table.mySeat], seat, table.mySeat, table);
}

/** True once the dealer's hand is fully revealed and no cards are still queued. */
function dealComplete(table: TableView): boolean {
  if (dealQueue.length > 0) return false;
  const dCards = table.dealer.cards ? table.dealer.cards.split(" ").filter(Boolean) : [];
  return faceCount(dealerHandEl!) >= dCards.length;
}

function dropQueuedFor(row: HTMLElement): void {
  dealQueue = dealQueue.filter((j) => j.row !== row);
}

/** Face cards currently shown in a row (excludes the face-down hole back). */
function faceCount(row: HTMLElement): number {
  return row.querySelectorAll("canvas:not(.gl-cardback)").length;
}

function ensureOverlay(): HTMLDivElement {
  if (overlay) return overlay;
  ensureStyle();

  overlay = document.createElement("div");
  overlay.id = "gl-bj-overlay";
  overlay.innerHTML = `
    <div id="gl-bj-scene">
      <div id="gl-bj-head">
        <h2>BLACKJACK</h2>
        <span id="gl-bj-phase"></span>
      </div>
      <div id="gl-bj-stage">
        <canvas id="gl-bj-table-img"></canvas>
        <div id="gl-bj-dealer">
          <canvas class="skeleton"></canvas>
          <div class="gl-bj-hand"></div>
          <span class="val"></span>
        </div>
      </div>
      <div id="gl-bj-myhand"></div>
      <div id="gl-bj-controls"></div>
      <div id="gl-bj-foot">
        <button class="gl-bj-btn primary" id="gl-bj-leave" type="button">Leave</button>
      </div>
    </div>`;

  // Paint the green felt table once; the dealer is a rendered character in a suit (same look as Roxy).
  paintInto(overlay.querySelector<HTMLCanvasElement>("#gl-bj-table-img")!, drawPokerTable());
  const dealerCanvas = overlay.querySelector<HTMLCanvasElement>("#gl-bj-dealer .skeleton")!;
  void composeBust(DEALER_LOOK).then((c) => {
    dealerCanvas.width = c.width; dealerCanvas.height = c.height;
    const ctx = dealerCanvas.getContext("2d")!; ctx.imageSmoothingEnabled = false; ctx.drawImage(c, 0, 0);
  });

  // Build the seat cards (each with two pre-made hand columns), around the arc.
  const stage = overlay.querySelector<HTMLDivElement>("#gl-bj-stage")!;
  seatNodes = [];
  for (let i = 0; i < NUM_SEATS; i++) {
    const node = document.createElement("div");
    node.className = "gl-bj-seat";
    node.style.left = `${SEAT_POS[i].l}%`;
    node.style.top = `${SEAT_POS[i].t}%`;
    node.innerHTML = `
      <div class="frame"></div>
      <span class="name"></span>
      <span class="badge" hidden></span>
      <div class="hands">
        <div class="handcol main" hidden><div class="gl-bj-hand"></div><span class="val"></span></div>
        <div class="handcol split" hidden><div class="gl-bj-hand"></div><span class="val"></span></div>
      </div>
      <span class="bet"></span>
      <span class="open-seat">OPEN SEAT</span>`;
    stage.appendChild(node);
    seatNodes.push(node);
  }

  dealerHandEl = overlay.querySelector<HTMLDivElement>("#gl-bj-dealer .gl-bj-hand")!;
  dealerValEl = overlay.querySelector<HTMLSpanElement>("#gl-bj-dealer .val")!;
  myHandEl = overlay.querySelector<HTMLDivElement>("#gl-bj-myhand")!;
  phaseEl = overlay.querySelector<HTMLDivElement>("#gl-bj-phase")!;
  controlsEl = overlay.querySelector<HTMLDivElement>("#gl-bj-controls")!;
  leaveBtn = overlay.querySelector<HTMLButtonElement>("#gl-bj-leave")!;
  leaveBtn.addEventListener("click", () => actions.leave?.());

  resultBanner = document.createElement("div");
  resultBanner.id = "gl-bj-result";
  overlay.appendChild(resultBanner);

  document.body.appendChild(overlay);
  blackjackStore.subscribe(render);
  window.setInterval(tickCountdown, 250);
  return overlay;
}

function paintInto(target: HTMLCanvasElement, src: HTMLCanvasElement): void {
  target.width = src.width;
  target.height = src.height;
  target.getContext("2d")!.drawImage(src, 0, 0);
}

function secondsLeft(table: TableView): number {
  if (!table.timerEndsAt) return 0;
  return Math.max(0, Math.ceil((table.timerEndsAt - Date.now()) / 1000));
}

function tickCountdown(): void {
  if (!current || !controlsEl) return;
  const el = controlsEl.querySelector<HTMLSpanElement>(".countdown");
  if (el) el.textContent = `${secondsLeft(current)}s`;
}

/**
 * Reconcile a hand row's cards with `codes` by ENQUEUING the newly-arrived cards
 * onto the paced deal queue (so they reveal one at a time). `dataset.faces` tracks
 * the committed set (queued or shown); the DOM lags behind as cards are dealt. A
 * trailing face-down `back` (the dealer hole) is queued too. When the new set isn't
 * an extension of the committed one (reset / split), the row is reset.
 */
function syncHand(row: HTMLDivElement, codes: string[], showBack: boolean): void {
  const committed = (row.dataset.faces ?? "").split(",").filter(Boolean);
  const extension = committed.length <= codes.length && committed.every((c, i) => codes[i] === c);
  const hadBack = row.dataset.back === "1";

  if (!extension) {
    dropQueuedFor(row);
    row.replaceChildren();
    row.dataset.faces = "";
    row.dataset.back = "0";
    enqueueFaces(row, codes, 0);
  } else {
    let from = committed.length;
    // Hole reveal: a face-down back stands in for the next card, which has now
    // arrived — flip it face-up in place instead of deleting it and dealing anew.
    if (hadBack && !showBack && codes.length > committed.length) {
      row.dataset.back = "0";
      const holeCode = codes[committed.length];
      enqueueDeal(row, () => flipHole(row, holeCode));
      from = committed.length + 1;
    }
    enqueueFaces(row, codes, from);
  }
  row.dataset.faces = codes.join(",");

  const hasBack = row.dataset.back === "1";
  if (showBack && !hasBack) {
    row.dataset.back = "1";
    enqueueDeal(row, () => {
      if (row.querySelector(".gl-cardback")) return;
      const b = drawCardBack();
      b.classList.add("gl-cardback", "gl-deal");
      row.appendChild(b);
    });
  } else if (!showBack && hasBack) {
    // A back with no card to flip into (rare) — just drop it.
    row.dataset.back = "0";
    row.querySelector<HTMLCanvasElement>(".gl-cardback")?.remove();
  }
}

/** Render a hand row DIRECTLY from server state — no deal animation, no paced queue. The
 *  seat (profile) cards are a plain reflection of the hand, so they update instantly the
 *  moment a card is dealt and stay identical across every player's view. (The staggered
 *  deal animation is reserved for the big bottom hand + the dealer.) */
function renderHandDirect(row: HTMLDivElement, codes: string[]): void {
  const sig = codes.join(",");
  if (row.dataset.directSig === sig) return; // unchanged — skip the redraw
  row.dataset.directSig = sig;
  dropQueuedFor(row); // cancel any queued animated reveals from a prior state
  row.replaceChildren(...codes.map((code) => drawCard(code)));
  row.dataset.faces = sig;
  row.dataset.back = "0";
}

/** Turn the dealer's face-down hole into its face card, flipping in place. */
function flipHole(row: HTMLDivElement, code: string): void {
  const back = row.querySelector<HTMLCanvasElement>(".gl-cardback");
  const face = drawCard(code);
  face.classList.add("gl-flip");
  if (back) row.replaceChild(face, back);
  else row.appendChild(face);
}

function enqueueFaces(row: HTMLDivElement, codes: string[], from: number): void {
  for (let i = from; i < codes.length; i++) {
    const code = codes[i];
    enqueueDeal(row, () => {
      const back = row.querySelector<HTMLCanvasElement>(".gl-cardback");
      const c = drawCard(code);
      c.classList.add("gl-deal");
      if (back) row.insertBefore(c, back);
      else row.appendChild(c);
    });
  }
}

function render(table: TableView): void {
  if (!overlay) return;
  current = table;
  phaseEl!.textContent = PHASE_LABEL[table.phase] ?? table.phase;

  // Queue seats + my hand first, then the dealer — so cards deal out player-first.
  for (let i = 0; i < NUM_SEATS; i++) {
    renderSeat(seatNodes[i], table.seats.find((s) => s.index === i), i, table);
  }
  renderMyHand(table);

  const holeHidden = table.phase === "dealing" || table.phase === "turns";
  syncHand(dealerHandEl!, table.dealer.cards ? table.dealer.cards.split(" ") : [], holeHidden && !!table.dealer.cards);

  refreshLabels(table);
  leaveBtn!.hidden = table.mySeat === -1;
  renderControls(table);
  maybeShowResult(table);
}

/** Toast the player's net result once, when their round settles. */
function maybeShowResult(table: TableView): void {
  if (table.phase !== "reset") {
    // A new round is underway — arm the toast again.
    if (table.phase === "betting" || table.phase === "idle" || table.phase === "dealing") {
      resultShown = false;
    }
    return;
  }
  if (resultShown) return;
  if (!dealComplete(table)) return; // wait for the dealer to finish dealing
  const seat = table.mySeat === -1 ? undefined : table.seats.find((s) => s.index === table.mySeat);
  if (!seat || seat.hand.status === "empty") return; // didn't play this round
  resultShown = true;
  const net = handNet(seat.hand) + (seat.hasSplit ? handNet(seat.splitHand) : 0);
  if (net > 0) showResultBanner("YOU WIN", "win", net);
  else if (net < 0) showResultBanner("YOU LOSE", "loss", -net);
  else showResultBanner("PUSH", "push");
}

/** Show the big centre-of-screen round result (replays the pop animation each time). */
function showResultBanner(word: string, kind: "win" | "loss" | "push", amount?: number): void {
  if (!resultBanner) return;
  resultBanner.className = ""; // reset so the animation can restart
  void resultBanner.offsetWidth; // force reflow → re-trigger the keyframes
  resultBanner.innerHTML = `<div class="word">${word}</div>` +
    (amount != null ? `<div class="amt">${cashIcon()} ${amount}</div>` : `<div class="amt">bet returned</div>`);
  resultBanner.classList.add(kind, "show");
}

/** Net change for a settled hand given its wager (matches the server payout rules). */
function handNet(hand: HandView): number {
  switch (hand.status) {
    case "blackjack":
      return Math.floor(hand.bet * 1.5);
    case "won":
      return hand.bet;
    case "lost":
    case "bust":
      return -hand.bet;
    default:
      return 0; // push / not settled
  }
}

/** Blackjack value of the given cards (aces 11, dropped to 1 to avoid busting). */
function cardsValue(cards: string[]): number {
  let total = 0;
  let aces = 0;
  for (const card of cards) {
    const rank = card[0];
    if (rank === "A") {
      total += 11;
      aces += 1;
    } else if (rank === "T" || rank === "J" || rank === "Q" || rank === "K") {
      total += 10;
    } else {
      total += Number(rank);
    }
  }
  while (total > 21 && aces > 0) {
    total -= 10;
    aces -= 1;
  }
  return total;
}

/** Set each hand's running value label, shown from the first revealed card. */
function refreshLabels(table: TableView): void {
  const dCards = table.dealer.cards ? table.dealer.cards.split(" ") : [];
  const dShown = faceCount(dealerHandEl!);
  // Running total of the dealer's face-up cards (the hole isn't counted until flipped).
  dealerValEl!.textContent = dShown > 0 ? String(cardsValue(dCards.slice(0, dShown))) : "";

  // A dealer-dependent result (won/lost/push/blackjack) may only be shown once the
  // dealer's hand is fully dealt; until then we just show the running hand value.
  const resultsReady = table.phase === "reset" && dealComplete(table);

  for (let i = 0; i < NUM_SEATS; i++) {
    const seat = table.seats.find((s) => s.index === i);
    if (!seat || !seat.occupied) continue;
    const node = seatNodes[i];
    labelCol(node.querySelector(".handcol.main"), seat.hand, resultsReady);
    labelCol(node.querySelector(".handcol.split"), seat.hasSplit ? seat.splitHand : undefined, resultsReady);
  }

  const me = table.mySeat === -1 ? undefined : table.seats.find((s) => s.index === table.mySeat);
  if (me && me.hand.status !== "empty") {
    const cols = myHandEl!.querySelectorAll<HTMLDivElement>(".handcol");
    labelCol(cols[0] ?? null, me.hand, resultsReady);
    labelCol(cols[1] ?? null, me.hasSplit ? me.splitHand : undefined, resultsReady);
  }
}

/** A result that hinges on the dealer's final hand, so it must stay hidden until the deal completes. */
function isDealerResult(status: string): boolean {
  return status === "won" || status === "lost" || status === "push" || status === "blackjack";
}

function labelCol(col: HTMLDivElement | null, hand: HandView | undefined, resultsReady: boolean): void {
  if (!col) return;
  const val = col.querySelector<HTMLSpanElement>(".val")!;
  if (!hand || hand.status === "empty") {
    val.textContent = "";
    val.className = "val";
    return;
  }
  const row = col.querySelector<HTMLDivElement>(".gl-bj-hand")!;
  const cards = hand.cards ? hand.cards.split(" ") : [];
  const shown = faceCount(row);
  if (shown === 0) {
    val.textContent = "";
    val.className = "val";
    return;
  }
  const fullyDealt = shown >= cards.length;
  // The final verdict shows only once fully dealt: a dealer-dependent result waits
  // for the dealer to finish, while a bust (independent of the dealer) shows at once.
  const settled = fullyDealt && ((isDealerResult(hand.status) && resultsReady) || hand.status === "bust");
  if (settled) {
    val.textContent = handLabel(hand);
    val.className = "val" + statusClass(hand.status);
    return;
  }
  // Otherwise show the running total of the cards revealed so far.
  val.textContent = String(cardsValue(cards.slice(0, shown)));
  val.className = "val";
}

function renderSeat(node: HTMLDivElement, seat: SeatView | undefined, index: number, table: TableView): void {
  const occupied = !!seat?.occupied;
  const mine = index === table.mySeat;
  const active = index === table.activeSeat;
  node.classList.toggle("empty", !occupied);
  node.classList.toggle("mine", mine);
  node.classList.toggle("active", active);

  const frame = node.querySelector<HTMLDivElement>(".frame")!;
  const main = node.querySelector<HTMLDivElement>(".handcol.main")!;
  const split = node.querySelector<HTMLDivElement>(".handcol.split")!;

  if (!occupied || !seat) {
    if (frame.dataset.sprite !== "") {
      frame.dataset.sprite = "";
      frame.replaceChildren();
    }
    updateHandcol(main, undefined, false);
    updateHandcol(split, undefined, false);
    return;
  }

  node.querySelector<HTMLSpanElement>(".name")!.textContent = seat.name;

  const look = appearanceHash(seat.appearance);
  if (frame.dataset.look !== look) {
    frame.dataset.look = look;
    void composeBust(seat.appearance).then((canvas) => {
      if (frame.dataset.look !== look) return; // seat re-occupied / changed mid-compose
      frame.replaceChildren(canvas);
    });
  }

  const badge = node.querySelector<HTMLSpanElement>(".badge")!;
  if (!seat.connected) setBadge(badge, "gone", "away");
  else if (mine) setBadge(badge, "you", "you");
  else if (seat.spectating) setBadge(badge, "spec", "waiting");
  else badge.hidden = true;

  // Every seat (including mine) shows its dealt cards in the profile card; my own hand is
  // ALSO mirrored large at the bottom. For MY seat, cap the reveal to the big hand's progress
  // so the profile card only shows a card AFTER it lands in my big hand.
  const limit = mine ? bigHandReveal() : undefined;
  updateHandcol(main, seat.hand, active && !table.activeSplitHand, false, limit?.main);
  updateHandcol(split, seat.hasSplit ? seat.splitHand : undefined, active && table.activeSplitHand, false, limit?.split);

  const bet = seat.hand.bet + (seat.hasSplit ? seat.splitHand.bet : 0) || seat.pendingBet;
  node.querySelector<HTMLSpanElement>(".bet")!.innerHTML = bet ? `${cashIcon()} ${bet}` : "";
}

/** Sync one hand column's cards (enqueue reveals) + active state, or hide it. The
 *  value label is set separately by refreshLabels once the hand is fully dealt. */
function updateHandcol(
  col: HTMLDivElement, hand: HandView | undefined, active: boolean,
  animated = false, revealLimit?: number,
): void {
  const row = col.querySelector<HTMLDivElement>(".gl-bj-hand")!;
  if (!hand || hand.status === "empty") {
    if (!col.hidden) {
      col.hidden = true;
      dropQueuedFor(row);
      row.replaceChildren();
      row.dataset.faces = "";
      row.dataset.back = "0";
      row.dataset.directSig = "";
    }
    return;
  }
  col.hidden = false;
  col.classList.toggle("active", active);
  const all = hand.cards ? hand.cards.split(" ") : [];
  // `revealLimit` caps how many cards show (the OWN seat follows the big hand's reveal so it
  // never leads). Seat cards reflect the hand directly (no animation) so they stay in sync for
  // everyone; only the big owner-only hand at the bottom keeps the staggered deal animation.
  const codes = revealLimit === undefined ? all : all.slice(0, revealLimit);
  if (animated) syncHand(row, codes, false);
  else renderHandDirect(row, codes);
}

/** How many cards the big bottom (owner) hand currently shows, per column. The OWN seat card
 *  never reveals past this, so the profile follows — never leads — the player's deal animation. */
function bigHandReveal(): { main: number; split: number } {
  const cols = myHandEl!.querySelectorAll<HTMLDivElement>(".handcol");
  const count = (c: HTMLDivElement | undefined): number =>
    c ? faceCount(c.querySelector<HTMLDivElement>(".gl-bj-hand")!) : 0;
  return { main: count(cols[0]), split: count(cols[1]) };
}

/** Mirror the acting player's hand(s) large at the bottom. */
function renderMyHand(table: TableView): void {
  const el = myHandEl!;
  const seat = table.mySeat === -1 ? undefined : table.seats.find((s) => s.index === table.mySeat);
  const inPlay = !!seat && seat.hand.status !== "empty";
  el.classList.toggle("show", inPlay);
  if (!inPlay || !seat) {
    if (el.dataset.sig !== "") {
      el.dataset.sig = "";
      el.replaceChildren();
    }
    return;
  }

  // Rebuild structure only when the set of hands changes; cards inside sync/animate.
  const sig = seat.hasSplit ? "2" : "1";
  if (el.dataset.sig !== sig) {
    el.dataset.sig = sig;
    el.replaceChildren();
    el.appendChild(bigHandCol());
    if (seat.hasSplit) el.appendChild(bigHandCol());
  }
  const cols = el.querySelectorAll<HTMLDivElement>(".handcol");
  updateHandcol(cols[0], seat.hand, table.activeSeat === table.mySeat && !table.activeSplitHand, true);
  if (seat.hasSplit && cols[1]) {
    updateHandcol(cols[1], seat.splitHand, table.activeSeat === table.mySeat && table.activeSplitHand, true);
  }
}

function bigHandCol(): HTMLDivElement {
  const col = document.createElement("div");
  col.className = "handcol";
  col.innerHTML = `<div class="gl-bj-hand"></div><span class="val"></span>`;
  return col;
}

function setBadge(badge: HTMLSpanElement, cls: string, text: string): void {
  badge.hidden = false;
  badge.className = `badge ${cls}`;
  badge.textContent = text;
}

function handLabel(hand: HandView): string {
  switch (hand.status) {
    case "blackjack":
      return "BJ!";
    case "bust":
      return "Bust";
    case "won":
      return `Won ${hand.value}`;
    case "lost":
      return `Lost ${hand.value}`;
    case "push":
      return `Push ${hand.value}`;
    default:
      return String(hand.value);
  }
}

function statusClass(status: string): string {
  if (status === "won" || status === "blackjack") return " won";
  if (status === "lost" || status === "bust") return " lost";
  if (status === "push") return " push";
  return "";
}

let lastControlsSig = "";

/** Everything that affects the CONTROLS' structure/content, EXCLUDING the live countdown
 *  seconds (tickCountdown updates those in place). render() runs on every state patch —
 *  including other players merely moving — and rebuilding the Bet/Hit buttons each time
 *  recreates them mid-click and swallows the press (the "click twice to bet" bug). So we
 *  only rebuild when this signature actually changes. */
function controlsSignature(table: TableView): string {
  const seat = table.mySeat === -1 ? undefined : table.seats.find((s) => s.index === table.mySeat);
  const hand = table.activeSplitHand ? seat?.splitHand : seat?.hand;
  return [
    table.phase, table.mySeat, table.activeSeat, table.activeSplitHand ? 1 : 0,
    seat?.spectating ? 1 : 0, seat?.pendingBet ?? 0, seat?.hasSplit ? 1 : 0,
    hand?.cards ?? "", balanceStore.get(), table.timerEndsAt ? 1 : 0,
  ].join("|");
}

function renderControls(table: TableView): void {
  const sig = controlsSignature(table);
  if (sig === lastControlsSig) return; // nothing relevant changed — keep the live buttons clickable
  lastControlsSig = sig;
  const el = controlsEl!;
  const mySeat = table.mySeat === -1 ? undefined : table.seats.find((s) => s.index === table.mySeat);
  const countdown = `<span class="countdown">${secondsLeft(table)}s</span>`;

  if (!mySeat) {
    el.innerHTML = `<span class="hint">${PHASE_LABEL[table.phase] ?? table.phase}</span>`;
    return;
  }

  if (table.phase === "betting") {
    if (mySeat.spectating) {
      el.innerHTML = `<span class="hint">Joining next round…</span> ${countdown}`;
      return;
    }
    if (mySeat.pendingBet > 0) {
      el.innerHTML = `<span class="hint">Bet placed: ${cashIcon()} ${mySeat.pendingBet} — waiting…</span> ${countdown}`;
      return;
    }
    renderBetStepper(el, countdown);
    return;
  }

  if (table.phase === "turns" && table.activeSeat === table.mySeat) {
    const hand = table.activeSplitHand ? mySeat.splitHand : mySeat.hand;
    const cards = hand.cards ? hand.cards.split(" ") : [];
    const balance = balanceStore.get();
    const twoCards = cards.length === 2;
    const canDbl = twoCards && balance >= hand.bet;
    const canSpl =
      twoCards && cards[0][0] === cards[1][0] && !mySeat.hasSplit && !table.activeSplitHand && balance >= hand.bet;

    let html = `
      <button class="gl-bj-action hit" id="gl-bj-hit" type="button">Hit</button>
      <button class="gl-bj-action stand" id="gl-bj-stand" type="button">Stand</button>`;
    if (canDbl) html += `<button class="gl-bj-action dbl" id="gl-bj-double" type="button">Double</button>`;
    if (canSpl) html += `<button class="gl-bj-action spl" id="gl-bj-split" type="button">Split</button>`;
    html += countdown;
    el.innerHTML = html;

    el.querySelector<HTMLButtonElement>("#gl-bj-hit")!.addEventListener("click", () => actions.hit?.());
    el.querySelector<HTMLButtonElement>("#gl-bj-stand")!.addEventListener("click", () => actions.stand?.());
    el.querySelector<HTMLButtonElement>("#gl-bj-double")?.addEventListener("click", () => actions.double?.());
    el.querySelector<HTMLButtonElement>("#gl-bj-split")?.addEventListener("click", () => actions.split?.());
    return;
  }

  const label =
    table.phase === "turns" ? "Waiting for other players…" : PHASE_LABEL[table.phase] ?? table.phase;
  el.innerHTML = `<span class="hint">${label}</span>${table.timerEndsAt ? ` ${countdown}` : ""}`;
}

function renderBetStepper(el: HTMLDivElement, countdown: string): void {
  const balance = balanceStore.get();
  const maxBet = Math.min(MAX_BET, Math.floor(balance / BET_STEP) * BET_STEP);
  selectedBet = Math.max(MIN_BET, Math.min(selectedBet, Math.max(MIN_BET, maxBet)));
  const canBet = balance >= MIN_BET;

  el.innerHTML = `
    <div class="gl-bj-stepper">
      <button class="gl-bj-step" id="gl-bj-minus" type="button">−</button>
      <span class="amt" title="Click to type a custom bet (max ${MAX_BET})">${cashIcon()} ${selectedBet}</span>
      <button class="gl-bj-step" id="gl-bj-plus" type="button">+</button>
    </div>
    <button class="gl-bj-action bet" id="gl-bj-bet" type="button">Place bet</button>
    ${countdown}`;

  const minus = el.querySelector<HTMLButtonElement>("#gl-bj-minus")!;
  const plus = el.querySelector<HTMLButtonElement>("#gl-bj-plus")!;
  const betBtn = el.querySelector<HTMLButtonElement>("#gl-bj-bet")!;
  const amt = el.querySelector<HTMLSpanElement>(".amt")!;
  betBtn.disabled = !canBet;
  if (!canBet) betBtn.textContent = "Not enough cash";

  // Adjust the wager in place — only the amount + step buttons change, so the
  // live countdown (ticked separately by tickCountdown) is never re-rendered,
  // which previously made it flash back to its full value on each click.
  let editing = false;
  function syncStepper(): void {
    editing = false;
    amt.innerHTML = `${cashIcon()} ${selectedBet}`;
    minus.disabled = selectedBet <= MIN_BET;
    plus.disabled = selectedBet >= maxBet;
  }
  syncStepper();

  minus.addEventListener("click", () => {
    selectedBet = Math.max(MIN_BET, selectedBet - BET_STEP);
    syncStepper();
  });
  plus.addEventListener("click", () => {
    selectedBet = Math.min(maxBet, selectedBet + BET_STEP);
    syncStepper();
  });

  // Click the amount to type a custom wager. Committed value is snapped to a
  // BET_STEP multiple and clamped to [MIN_BET, maxBet] (so it can never exceed
  // the table limit or the player's balance).
  function commitTyped(raw: string): void {
    const n = parseInt(raw, 10);
    if (!Number.isNaN(n)) {
      const snapped = Math.round(n / BET_STEP) * BET_STEP;
      selectedBet = Math.max(MIN_BET, Math.min(maxBet, snapped));
    }
    syncStepper();
  }
  amt.addEventListener("click", () => {
    if (editing || !canBet) return;
    editing = true;
    amt.innerHTML =
      `<input class="amt-input" type="number" inputmode="numeric" ` +
      `min="${MIN_BET}" max="${maxBet}" step="${BET_STEP}" value="${selectedBet}">`;
    const input = amt.querySelector<HTMLInputElement>(".amt-input")!;
    input.focus();
    input.select();
    input.addEventListener("keydown", (ev) => {
      ev.stopPropagation(); // keep digits out of the game's movement/hotbar keys
      if (ev.key === "Enter") { ev.preventDefault(); commitTyped(input.value); }
      else if (ev.key === "Escape") { ev.preventDefault(); syncStepper(); }
    });
    input.addEventListener("blur", () => { if (editing) commitTyped(input.value); });
  });
  betBtn.addEventListener("click", () => actions.bet?.(selectedBet));
}

export function openTable(): void {
  lastControlsSig = ""; // force the controls to rebuild on (re)open
  ensureOverlay().classList.add("open");
}

export function closeTable(): void {
  overlay?.classList.remove("open");
}

export function isTableOpen(): boolean {
  return !!overlay?.classList.contains("open");
}
